import { connect } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next"
import { toast } from "react-toastify";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Col,
    Row,
    Label
} from "reactstrap";
import Select from 'react-select';
import EntityRoutingService from "src/modules/3rd-party-management/apis/admin/EntityRoutingService";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import UserUtils from "src/services/utils/UserUtils";
import { useCallback, useEffect, useState } from "react";

const RoutingModal = ({
    currentUser,
    t,
    toggle,
    show,
    onComplete,
    prevValues
}) => {
    const isEditing = !!prevValues?.legal_entity?.id;

    const [ entity, setEntity ] = useState(null);
    const [ routingSettings, setRoutingSettings ] = useState({
        defaultSupplierOwner: null,
        defaultSupplierRiskOwner: null,
        defaultSupplierTaskOwner: null
    });

    const [ errors, setErrors ] = useState({
        entity: false,
        defaultSupplierOwner: false,
        defaultSupplierRiskOwner: false,
        defaultSupplierTaskOwner: false
    })

    const userUtils = new UserUtils();
    const entityService = EntityRoutingService.getInstance();
    const commonService = CommonService.getInstance();
    
    const isAdmin = userUtils.isAnalystAdmin(currentUser);

    const entitiesQuery = useQuery({
        queryKey: [
            "3rd-party-fetch-settings-not-routed-entities-list"
        ],
        queryFn: async () => {
            const response = await entityService.fetchUnRoutedEntitiesList({
                pageIndex: 1,
                pageSize: 1000
            });

            return (response?.legalEntities || []).map((entity) => {
                return {
                    value: entity.id,
                    label: `${entity.title} (${entity.shortName})`
                }
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: show,
        onError: () => {
            toast(t("An error occurred while fetching entities."), {
                type: "error"
            });
        },
    });

    const {
        data: analysts,
        isFetching: analystsAreFetching
    } = useQuery({
        queryKey: ["fetch-analyst-and-admin-analyst-list"],
        queryFn: async () => {
            const response = await commonService.fetchAnalystAndAdminAnalystList();

            return (response || []).map((user) => {
                return {
                    value: user.id,
                    label: `${user.first_name} ${user.last_name}`
                }
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching analyst admins list."), {
                type: "error"
            });
        },
    });

    const saveSettingsMutation = useMutation({
        mutationFn: async () => {
            return await entityService.saveRouterSettings(entity.value, Object.keys(routingSettings).reduce((acc, key) => {
                acc[key] = routingSettings[key]?.value || null;
                return acc;
            }, {}));
        },
        onError: () => {
            toast(t("An error occurred while saving settings."), {
                type: "error"
            });
        },
        onSuccess: () => {
            toast(t("Settings saved successfully."), {
                type: "success"
            });

            onComplete();
            toggle();
        }
    });

    const fetchRoutingDetailsMutation = useMutation({
        mutationFn: async (id) => {
            return await entityService.fetchRoutingDetails(id);
        },
        onError: () => {
            toast(t("An error occurred while fetching routing details."), {
                type: "error"
            });
        },
        onSuccess: (data) => {
            if (Array.isArray(data.data)) {
                setRoutingSettings({
                    defaultSupplierOwner: null,
                    defaultSupplierRiskOwner: null,
                    defaultSupplierTaskOwner: null
                });
            }
            else{
                if(data?.data && typeof data.data === 'object'){
                    setRoutingSettings({
                        defaultSupplierOwner: {
                            value: data.data.default_supplier_owner?.id,
                            label: data.data.default_supplier_owner?.full_name,
                        },
                        defaultSupplierRiskOwner: {
                            value: data.data.default_supplier_risk_owner?.id,
                            label: data.data.default_supplier_risk_owner?.full_name,
                        },
                        defaultSupplierTaskOwner: {
                            value: data.data.default_supplier_task_owner?.id,
                            label: data.data.default_supplier_task_owner?.full_name,
                        }
                    });
                }
            }
        }
    });

    const handleRoutingChange = useCallback((settingKey, value) => {
        setRoutingSettings((currentState) => {
            return {
                ...currentState,
                [settingKey]: value
            }
        });

        setErrors((currentState) => {
            return {
                ...currentState,
                [settingKey]: false
            }
        })
    }, []);

    const handleSaveSettings = () => {
        const currentErrors = {
            entity: !isEditing ? !entity?.value : false
        }; 

        Object.keys(routingSettings).forEach((key) => {
            currentErrors[key] = !routingSettings[key]?.value
        });

        setErrors(currentErrors);

        if(!Object.keys(currentErrors).filter((key) => currentErrors[key]).length){
            saveSettingsMutation.mutate();
        }
    }

    useEffect(() => {
        if(show){
            setEntity(null);
            setRoutingSettings({
                defaultSupplierOwner: null,
                defaultSupplierRiskOwner: null,
                defaultSupplierTaskOwner: null
            });
            setErrors({
                entity: false,
                defaultSupplierOwner: false,
                defaultSupplierRiskOwner: false,
                defaultSupplierTaskOwner: false
            })
        }
    }, [ show ]);

    useEffect(() => {
        if(prevValues?.legal_entity) {
            setEntity({
                value: prevValues.legal_entity.id,
                label: prevValues.legal_entity.title
            });
            
            fetchRoutingDetailsMutation.mutate(prevValues.legal_entity.id);
        }
    }, [ prevValues ])

    return (
        <Modal 
            size="md" 
            isOpen={show}
        >
            <ModalHeader 
                className="border-0" 
                toggle={toggle}
            >
                {t(isEditing ? "Edit Routing Settings" : "Add Routing Settings")}
            </ModalHeader>

            <ModalBody>
                <Row className="mb-3">
                    <Col sm='12' className="mb-3">
                        <Label>    
                            {t("Entity")}
                        </Label>
                        {!isEditing ? (
                            <Select
                                options={entitiesQuery.data || []}
                                isLoading={entitiesQuery.isFetching}
                                value={routingSettings.entity}
                                onChange={(e) => {
                                    setEntity(e);
                                    setErrors((currentState) => {
                                        return {
                                            ...currentState,
                                            entity: false
                                        }
                                    })
                                }}
                                placeholder={t("Select Entity")}
                                isDisabled={!isAdmin || entitiesQuery.isFetching}
                            />
                        ) : (
                            <p className="mb-0">
                                {prevValues.legal_entity.title}
                            </p>
                        )}
                        {errors.entity && (
                            <span className="invalid-feedback d-block">
                                {t('This field is required')}
                            </span>
                        )}
                    </Col>

                    <Col sm='12' className="mb-3">
                        <Label>    
                            {t("Default Owner")}
                        </Label>
                        <Select
                            options={analysts}
                            isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                            value={routingSettings.defaultSupplierOwner}
                            onChange={(e) => handleRoutingChange('defaultSupplierOwner', e)}
                            placeholder={t("Select...")}
                            isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                        />
                        {errors.defaultSupplierOwner && (
                            <span className="invalid-feedback d-block">
                                {t('This field is required')}
                            </span>
                        )}
                    </Col>

                    <Col sm='12' className="mb-3">
                        <Label>    
                            {t("Assign Risks To")}
                        </Label>
                        <Select
                            options={analysts}
                            isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                            value={routingSettings.defaultSupplierRiskOwner}
                            onChange={(e) => handleRoutingChange('defaultSupplierRiskOwner', e)}
                            placeholder={t("Select...")}
                            isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                        />
                        {errors.defaultSupplierRiskOwner && (
                            <span className="invalid-feedback d-block">
                                {t('This field is required')}
                            </span>
                        )}
                    </Col>

                    <Col sm='12'>
                        <Label>    
                            {t("Assign Tasks To")}
                        </Label>
                        <Select
                            options={analysts}
                            isLoading={analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                            value={routingSettings.defaultSupplierTaskOwner}
                            onChange={(e) => handleRoutingChange('defaultSupplierTaskOwner', e)}
                            placeholder={t("Select...")}
                            isDisabled={!isAdmin || analystsAreFetching || fetchRoutingDetailsMutation.isLoading}
                        />
                        {errors.defaultSupplierTaskOwner && (
                            <span className="invalid-feedback d-block">
                                {t('This field is required')}
                            </span>
                        )}
                    </Col>
                </Row>
            </ModalBody>

            <ModalFooter>
                <Button 
                    color="danger" 
                    onClick={toggle} 
                    disabled={false}
                >
                    {t('Cancel')}
                </Button>

                <Button 
                    color="primary" 
                    onClick={handleSaveSettings}
                    disabled={
                        saveSettingsMutation.isLoading || 
                        entitiesQuery.isFetching || 
                        fetchRoutingDetailsMutation.isLoading ||
                        analystsAreFetching
                    }
                >
                    {
                        saveSettingsMutation.isLoading && (
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                size="sm" 
                                className="me-1"
                            />
                        )
                    }
                    {t(isEditing ? 'Save' : 'Add Routing')}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

const mapStatetoProps = (state) => {
    const { user: currentUser } = state.Login;
    return {
        currentUser
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        RoutingModal
    )
);